import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AgGridReact } from 'ag-grid-react'
import {
  CellValueChangedEvent,
  FirstDataRenderedEvent,
  GridApi,
  GridReadyEvent,
} from 'ag-grid-community'
import { styled } from '@mui/material'
import _ from 'lodash'
import { useIntl } from 'react-intl'
import { PageArea, PageProps } from '..'
import { BulkSheetView } from '../../containers/BulkSheetView'
import { projectPrivate } from '../../higher-order-components/projectPrivate'
import Loading from '../../components/process-state-notifications/Loading'
import CancelConfirmDialog, {
  useCancelConfirmDialog,
} from '../../components/dialogs/CancelConfirmDialog'
import ReloadButton from '../../containers/BulkSheetView/components/header/ReloadButton'
import FilterButton from '../../containers/BulkSheetView/components/header/FilterButton'
import SortButton from '../../containers/BulkSheetView/components/header/SortButton'
import ColumnSettingButton from '../../containers/BulkSheetView/components/header/ColumnSettingButton'
import DisplayFormatButton from '../../containers/BulkSheetView/components/header/DisplayFormatButton'
import ColumnSettingPopper from '../../containers/BulkSheetView/components/columnSelector/ColumnSettingPopper'
import SavePopper from '../../containers/BulkSheetView/components/header/SaveButtonArea'
import { useBulkSheetState } from '../../containers/BulkSheetView/hooks/bulkSheetState/bulkSheetState'
import { useColumnState } from '../../containers/BulkSheetView/hooks/columnState/useColumnState'
import { useColumnSetting } from '../../containers/BulkSheetView/components/columnSelector/useColumnSetting'
import { UiStateKey } from '../../../lib/commons/uiStates'
import { usePageState } from '../../hooks/usePageState'
import { useKeyBind } from '../../hooks/useKeyBind'
import { KEY_SAVE } from '../../model/keyBind'
import { useWorkloadUnit } from '../../hooks/useWorkloadUnit'
import store from '../../../store'
import { requireSave } from '../../../store/requiredSaveData'
import { addGlobalMessage, MessageLevel } from '../../../store/messages'
import { AggregateField } from '../../../domain/entity/WbsItemEntity'
import { WorkloadUnit } from '../../../lib/functions/workload'
import { DISPLAY_DATE_FORMAT } from '../../../utils/date'
import { ROW_HEIGHT } from '../../containers/BulkSheet'
import { useProgramProjectReportGridOptions } from './gridOptions'
import { useProgramProjectReportData } from './hooks/useProgramProjectReportData'
import { useUnitSetting } from './hooks/useUnitSetting'
import {
  aggregateProgressBottomUp,
  PROGRAM_PROJECT_REPORT_INPUT_MODE,
  ProgramProjectReportInputMode,
  ProgramProjectReportRow,
  recomputeAncestorsProgress,
} from './model/reportRow'
import { useProgramProjectReportContextMenu } from './hooks/useContextMenu'
import { UnitHeader } from './components/UnitHeader'
import { UnitSettingDialog } from './components/UnitSettingDialog'
import { useProjectPrivateContext } from '../../context/projectContext'

const ProgramProjectReportPage = ({ uuid }: PageProps) => {
  const intl = useIntl()
  const { projectUuid } = useProjectPrivateContext()
  const gridRef = useRef<AgGridReact<ProgramProjectReportRow>>(null)
  const bulkSheetRef = useRef<HTMLDivElement>(null)
  const [gridApi, setGridApi] = useState<GridApi>()
  const [saving, setSaving] = useState(false)
  const { data, loading, fetch, save, savedAggregateField } =
    useProgramProjectReportData()

  const { workloadUnit, inputMode, dateFormat, rowHeight, updatePageState } =
    usePageState<{
      workloadUnit: WorkloadUnit
      inputMode: ProgramProjectReportInputMode
      dateFormat: string
      rowHeight: number
    }>(
      uuid,
      {
        workloadUnit: WorkloadUnit.HOUR,
        inputMode: PROGRAM_PROJECT_REPORT_INPUT_MODE.AGGREGATION,
        dateFormat: DISPLAY_DATE_FORMAT,
        rowHeight: ROW_HEIGHT.SMALL,
      },
      s => s,
      projectUuid
    )
  const setWorkloadUnit = useCallback(
    (unit: WorkloadUnit) => updatePageState({ workloadUnit: unit }),
    [updatePageState]
  )
  const setDateFormat = useCallback(
    (format: string) => updatePageState({ dateFormat: format }),
    [updatePageState]
  )
  const setRowHeight = useCallback(
    (value: number) => updatePageState({ rowHeight: value }),
    [updatePageState]
  )
  const unitSetting = useUnitSetting(
    savedAggregateField,
    workloadUnit,
    setWorkloadUnit
  )
  const contextMenu = useProgramProjectReportContextMenu({
    allRows: data,
    inputMode,
  })
  const gridOptions = useProgramProjectReportGridOptions()
  const workloadUnitState = useWorkloadUnit(workloadUnit)
  const gridContext = useMemo(
    () => ({
      workloadUnitState,
      aggregateField: unitSetting.aggregateField,
      inputMode,
      allRows: data,
      dateFormat,
    }),
    [workloadUnitState, unitSetting.aggregateField, inputMode, data, dateFormat]
  )
  useEffect(() => {
    gridApi?.refreshCells({ force: true })
    gridApi?.refreshHeader()
  }, [gridApi, gridContext])
  useEffect(() => {
    if (!gridApi) return
    const rafId = requestAnimationFrame(() => {
      gridApi.resetRowHeights()
    })
    return () => cancelAnimationFrame(rafId)
  }, [gridApi, rowHeight])
  const [isChangeDialogOpen, setChangeDialogOpen] = useState(false)
  const isInitialDialogOpen = !loading && !unitSetting.isUnitSettled

  const cancelConfirmDialog = useCancelConfirmDialog(fetch)
  const { openDialog } = cancelConfirmDialog
  const handleReload = useCallback(() => {
    if (store.getState().hasRequiredSaveData.hasRequiredSaveData) {
      openDialog()
      return
    }
    fetch()
  }, [fetch, openDialog])

  const bulkSheetState = useBulkSheetState(
    uuid,
    UiStateKey.ProgramProjectReportColumnAndFilterState
  )
  const columnState = useColumnState({
    gridApi,
    autoGroupColumnDef: gridOptions.autoGroupColumnDef,
    bulkSheetState,
  })
  const columnSetting = useColumnSetting()

  const handleGridReady = useCallback(
    (event: GridReadyEvent) => {
      setGridApi(event.api)
      if (!_.isEmpty(bulkSheetState.columnState)) {
        event.api.applyColumnState({
          state: bulkSheetState.columnState,
          applyOrder: true,
        })
        return
      }
      const columnState = event.api.getColumnState()
      const nameIndex = columnState.findIndex(
        state => state.colId === 'ag-Grid-AutoColumn'
      )
      const statusIndex = columnState.findIndex(
        state => state.colId === 'status'
      )
      if (nameIndex < 0 || statusIndex < 0) return
      const [nameState] = columnState.splice(nameIndex, 1)
      const insertIndex =
        columnState.findIndex(state => state.colId === 'status') + 1
      columnState.splice(insertIndex, 0, nameState)
      event.api.applyColumnState({ state: columnState, applyOrder: true })
    },
    [bulkSheetState.columnState]
  )

  const onFirstDataRendered = useCallback(
    (event: FirstDataRenderedEvent) => {
      if (!_.isEmpty(bulkSheetState.filterState)) {
        event.api.setFilterModel(bulkSheetState.filterState ?? null)
      }
      bulkSheetState.finishRestoring()
    },
    [bulkSheetState.filterState, bulkSheetState.finishRestoring]
  )

  const onCellValueChanged = useCallback(
    (event: CellValueChangedEvent<ProgramProjectReportRow>) => {
      if (
        inputMode === PROGRAM_PROJECT_REPORT_INPUT_MODE.AGGREGATION &&
        event.data
      ) {
        recomputeAncestorsProgress(data, event.data)
      }
      gridApi?.refreshCells({ force: true })
      store.dispatch(requireSave())
    },
    [inputMode, data, gridApi]
  )

  const persist = useCallback(
    async (
      aggregateField: AggregateField,
      options?: { clearProgress?: boolean }
    ): Promise<boolean> => {
      setSaving(true)
      try {
        const response = await save(aggregateField, options)
        if (!response.hasError && !response.hasWarning) {
          store.dispatch(
            addGlobalMessage({
              type: MessageLevel.SUCCESS,
              title: intl.formatMessage({ id: 'registration.complete' }),
            })
          )
          await fetch()
          return true
        }
        return false
      } finally {
        setSaving(false)
      }
    },
    [save, fetch, intl]
  )

  const onSubmit = useCallback(async () => {
    gridApi?.stopEditing()
    await persist(unitSetting.aggregateField)
  }, [gridApi, unitSetting.aggregateField, persist])

  const applyInputModeChange = useCallback(
    (newInputMode: ProgramProjectReportInputMode) => {
      if (newInputMode === PROGRAM_PROJECT_REPORT_INPUT_MODE.AGGREGATION) {
        aggregateProgressBottomUp(data)
      }
      updatePageState({ inputMode: newInputMode })
    },
    [data, updatePageState]
  )

  const clearActualResourceOnCount = useCallback(
    (field: AggregateField) => {
      if (field === AggregateField.WBS_ITEM_COUNT) {
        data.forEach(row => {
          if (row.progress) {
            row.progress.actualResource = null
          }
        })
      }
    },
    [data]
  )

  const onSettleUnit = useCallback(
    async (
      field: AggregateField,
      unit: WorkloadUnit,
      _deleteExistingData: boolean,
      newInputMode?: ProgramProjectReportInputMode
    ) => {
      unitSetting.selectUnit(field, unit)
      clearActualResourceOnCount(field)
      if (newInputMode) {
        applyInputModeChange(newInputMode)
      }
      await persist(field)
    },
    [unitSetting, persist, applyInputModeChange, clearActualResourceOnCount]
  )

  const onChangeUnit = useCallback(
    async (
      field: AggregateField,
      unit: WorkloadUnit,
      deleteExistingData: boolean,
      newInputMode?: ProgramProjectReportInputMode
    ) => {
      unitSetting.selectUnit(field, unit)
      clearActualResourceOnCount(field)
      setChangeDialogOpen(false)

      if (newInputMode && newInputMode !== inputMode) {
        applyInputModeChange(newInputMode)
        gridApi?.refreshCells({ force: true })
      }

      await persist(field, { clearProgress: deleteExistingData })
    },
    [
      unitSetting,
      persist,
      inputMode,
      applyInputModeChange,
      clearActualResourceOnCount,
      gridApi,
    ]
  )

  useKeyBind(
    [{ key: KEY_SAVE, fn: onSubmit, stopDefaultBehavior: true }],
    [onSubmit]
  )

  return (
    <PageArea>
      <SavePopper loading={saving} onSubmit={onSubmit} />
      <Header>
        <ReloadButton onReload={handleReload} />
        {unitSetting.isUnitSettled && (
          <UnitHeader
            aggregateField={unitSetting.aggregateField}
            workloadUnit={unitSetting.workloadUnit}
            inputMode={inputMode}
            onChangeWorkloadUnit={unitSetting.setWorkloadUnit}
            onClickChange={() => setChangeDialogOpen(true)}
          />
        )}
        <FilterButton
          filteredColumns={columnState.filteredColumns}
          onDeleteFilteredColumn={columnState.onDeleteFilteredColumn}
          resetFilters={columnState.resetFilters}
        />
        <SortButton
          onDeleteSortedColumn={columnState.onDeleteSortedColumn}
          onDeleteSortedAllColumns={columnState.onDeleteSortedAllColumns}
          onChangeSortColumnState={columnState.onChangeSortColumnState}
          sortColumnsState={columnState.sortColumnsState}
        />
        <ColumnSettingButton
          onClick={columnSetting.toggle}
          open={columnSetting.isOpen}
        />
        <DisplayFormatButton
          height={rowHeight}
          onChangeHeight={setRowHeight}
          currentFormat={dateFormat}
          onChangeDateFormat={setDateFormat}
        />
      </Header>
      {bulkSheetState.initialized && (
        <BulkSheetView
          ref={bulkSheetRef}
          gridRef={gridRef}
          gridOptions={gridOptions}
          context={gridContext}
          rowData={data}
          rowHeight={rowHeight}
          onGridReady={handleGridReady}
          onFirstDataRendered={onFirstDataRendered}
          getContextMenuItems={contextMenu}
          onCellValueChanged={onCellValueChanged}
          onColumnVisible={columnState.onColumnVisible}
          onColumnResized={columnState.rememberColumnState}
          onColumnMoved={columnState.rememberColumnState}
          onFilterChanged={columnState.onFilterChanged}
          onSortChanged={columnState.onSortChanged}
        />
      )}
      <Loading
        isLoading={!bulkSheetState.initialized || loading}
        elem={bulkSheetRef.current}
      />

      <UnitSettingDialog
        mode="initial"
        isOpen={isInitialDialogOpen}
        defaultField={unitSetting.aggregateField}
        defaultWorkloadUnit={WorkloadUnit.HOUR}
        onSubmit={onSettleUnit}
      />
      <UnitSettingDialog
        mode="change"
        isOpen={isChangeDialogOpen}
        defaultField={unitSetting.aggregateField}
        defaultWorkloadUnit={unitSetting.workloadUnit}
        currentInputMode={inputMode}
        onSubmit={onChangeUnit}
        onClose={() => setChangeDialogOpen(false)}
      />
      {columnSetting.isOpen && (
        <ColumnSettingPopper
          anchorEl={columnSetting.anchorEl}
          open={columnSetting.isOpen}
          close={columnSetting.close}
          gridApi={gridApi}
          height={bulkSheetRef.current?.offsetHeight}
          openSavedUiStateDialog={undefined}
          initializeColumnState={columnState.resetColumnState}
          applicationFunctionUuid={uuid}
          uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
          columnState={bulkSheetState.columnState}
          offset={120}
        />
      )}
      <CancelConfirmDialog
        open={cancelConfirmDialog.open}
        onConfirm={cancelConfirmDialog.onConfirm}
        onClose={cancelConfirmDialog.onClose}
      />
    </PageArea>
  )
}

const Header = styled('div')({
  display: 'flex',
  alignItems: 'center',
  margin: '10px 10px 5px 10px',
  overflowX: 'auto',
  overflowY: 'hidden',
  minHeight: '47px',
})

export const ProgramProjectReport = projectPrivate(ProgramProjectReportPage)
